import { Badge, Box, Button, Drawer, Icon, Theme } from "@outschool/backpack";
import {
  ThreadsForSectionQuery,
  ThreadsForSectionQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  PostType,
  useMessageTeacher,
} from "../../providers/MessageTeacherProvider";
import { useLearnerClassroomAssignmentsBySectionQuery } from "../../queries/LearnerClassroomAssignmentsBySectionQueries";
import { THREADS_FOR_SECTION_QUERY } from "../../queries/ThreadsForSectionQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS, VIEW } from "./constants";
import FeedbackThreadDetailView from "./FeedbackThreadDetailView";
import { MessageTeacherDrawerProvider } from "./MessageTeacherDrawerContext";
import NewThreadView from "./NewThreadView";
import ThreadDetailView from "./ThreadDetailView";
import ThreadListView from "./ThreadListView";
import {
  ConversationListItem,
  DrawerView,
  FeedbackThreadItem,
  PostTarget,
} from "./types";
import {
  getConversationDetailView,
  getThreadPostTitle,
  isGeneralThread,
  toBubbleMessageFromAssignmentComment,
} from "./utils";

interface MessageTeacherButtonProps {
  sectionUid: string;
}

export default function MessageTeacherButton({
  sectionUid,
}: MessageTeacherButtonProps) {
  const { t } = useTranslation("learnerApp\\MessageTeacherButton");
  const isMobile = useIsMobile();
  const currentLearner = useCurrentLearner();
  const { registerOpenForPost } = useMessageTeacher();
  const learnerInfo = {
    uid: currentLearner!.uid,
    name: currentLearner?.name ?? "",
    avatar: currentLearner?.avatar ?? null,
  };
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<DrawerView>({ kind: VIEW.LIST });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    loading: threadsLoading,
    refetch,
  } = useQueryWithPreviousData<
    ThreadsForSectionQuery,
    ThreadsForSectionQueryVariables
  >(THREADS_FOR_SECTION_QUERY, {
    variables: {
      sectionUid,
      searchQuery: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
  });

  const unreadCount = data?.section?.unreadThreadsCount ?? 0;
  const leader = data?.section?.leader;
  const threads = useMemo(
    () => data?.section?.threads ?? [],
    [data?.section?.threads],
  );
  const { data: assignmentData, loading: feedbackThreadsLoading } =
    useLearnerClassroomAssignmentsBySectionQuery(
      { sectionUid },
      { skip: !open },
    );
  const sectionEndTime = data?.section?.end_time ?? null;
  const feedbackThreads = useMemo<FeedbackThreadItem[]>(() => {
    const assignments = assignmentData?.section?.assignmentClassPosts ?? [];
    const lowerSearch = debouncedSearch.trim().toLowerCase();

    return assignments.flatMap(assignment => {
      const submission = assignment.comments.find(
        comment =>
          comment.isSubmission === true &&
          comment.sender.uid === learnerInfo.uid,
      );
      if (!submission) {
        return [];
      }

      const messageNodes = [submission, ...(submission.replies ?? [])];
      const messages = messageNodes
        .map(toBubbleMessageFromAssignmentComment)
        .sort((a, b) => a.sentAt.localeCompare(b.sentAt));

      if (messages.length === 0) {
        return [];
      }

      const assignmentTitle =
        assignment.title?.trim() || t("Untitled assignment");
      if (lowerSearch) {
        const titleMatches = assignmentTitle
          .toLowerCase()
          .includes(lowerSearch);
        const contentMatches = messages.some(message =>
          message.messageContent.text?.toLowerCase().includes(lowerSearch),
        );
        if (!titleMatches && !contentMatches) {
          return [];
        }
      }

      return [
        {
          kind: "feedback" as const,
          uid: `feedback-${assignment.uid}-${submission.uid}`,
          assignmentClassPostUid: assignment.uid,
          assignmentTitle,
          latestMessage: messages[messages.length - 1] ?? null,
          messages,
          unreadCount: 0 as const,
        },
      ];
    });
  }, [
    assignmentData?.section?.assignmentClassPosts,
    debouncedSearch,
    learnerInfo.uid,
    t,
  ]);

  const openForPost = useCallback(
    (postUid: string, postType: PostType, postTitle: string) => {
      const match = threads.find(thread => {
        if (postType === "assignmentPost") {
          return thread.learningAssignmentClassPost?.uid === postUid;
        }
        return thread.classPost?.uid === postUid;
      });

      if (match) {
        setView({
          kind: VIEW.THREAD,
          threadUid: match.uid,
          postTitleOverride: null,
        });
      } else {
        const postTarget: PostTarget =
          postType === "classPost"
            ? { classPostUid: postUid }
            : { learningAssignmentClassPostUid: postUid };
        setView({
          kind: VIEW.NEW_THREAD,
          pendingPost: { kind: "post", postTarget, postTitle },
        });
      }
      setOpen(true);
    },
    [threads],
  );

  useEffect(() => {
    registerOpenForPost(openForPost);
  }, [registerOpenForPost, openForPost]);

  const handleClose = () => {
    setOpen(false);
    setView({ kind: VIEW.LIST });
    setSearchInput("");
    setDebouncedSearch("");
  };

  const handleThreadCreated = (threadUid: string) => {
    const postTitleOverride =
      view.kind === VIEW.NEW_THREAD && view.pendingPost.kind === "post"
        ? view.pendingPost.postTitle
        : null;
    setView({ kind: VIEW.THREAD, threadUid, postTitleOverride });
    refetch();
  };

  const selectedThread =
    view.kind === VIEW.THREAD
      ? threads.find(t => t.uid === view.threadUid)
      : null;
  const selectedFeedbackThread =
    view.kind === VIEW.FEEDBACK
      ? feedbackThreads.find(thread => thread.uid === view.feedbackThreadUid) ??
        null
      : null;
  const teacherName = leader?.name?.trim() || t("Teacher");

  const sectionInfo = { uid: sectionUid, end_time: sectionEndTime };

  const handleOpenGeneralThread = () => {
    const existingGeneral = threads.find(isGeneralThread);
    if (existingGeneral) {
      setView({
        kind: VIEW.THREAD,
        threadUid: existingGeneral.uid,
        postTitleOverride: null,
      });
    } else {
      setView({ kind: VIEW.NEW_THREAD, pendingPost: { kind: "general" } });
    }
  };

  const drawerContextValue = {
    section: sectionInfo,
    learner: learnerInfo,
    leader: leader ?? null,
    teacherName,
    threads,
    feedbackThreads,
    threadsLoading,
    feedbackThreadsLoading,
    searchInput,
    debouncedSearch,
    setSearchInput,
  };

  const handleConversationClick = useCallback((item: ConversationListItem) => {
    setView(getConversationDetailView(item));
  }, []);

  return (
    <>
      {isMobile ? (
        <Badge badgeContent={unreadCount} color="error">
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Icon icon={faComment} sx={{ fontSize: "1.5rem" }} />
          </Button>
        </Badge>
      ) : (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 8 }}>
            <UserHeadshotImage user={leader} size={24} />
          </Badge>
          <Box component="span">{t("All Messages")}</Box>
        </Button>
      )}
      <Drawer
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme: Theme) => ({
            width: 400,
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              width: "85%",
            },
          }),
        }}
      >
        <MessageTeacherDrawerProvider value={drawerContextValue}>
          {view.kind === VIEW.THREAD ? (
            <ThreadDetailView
              threadUid={view.threadUid}
              postTitle={
                getThreadPostTitle(selectedThread) ?? view.postTitleOverride
              }
              onBack={() => setView({ kind: VIEW.LIST })}
              onClose={handleClose}
            />
          ) : view.kind === VIEW.FEEDBACK ? (
            selectedFeedbackThread ? (
              <FeedbackThreadDetailView
                feedbackThread={selectedFeedbackThread}
                onBack={() => setView({ kind: VIEW.LIST })}
                onClose={handleClose}
              />
            ) : (
              <ThreadListView
                onConversationClick={handleConversationClick}
                onOpenGeneralThread={handleOpenGeneralThread}
                onClose={handleClose}
              />
            )
          ) : view.kind === VIEW.NEW_THREAD ? (
            <NewThreadView
              {...(view.pendingPost.kind === "post"
                ? view.pendingPost.postTarget
                : {})}
              postTitle={
                view.pendingPost.kind === "post"
                  ? view.pendingPost.postTitle
                  : undefined
              }
              hideBackButton={false}
              onBack={() => setView({ kind: VIEW.LIST })}
              onClose={handleClose}
              onThreadCreated={handleThreadCreated}
            />
          ) : (
            <ThreadListView
              onConversationClick={handleConversationClick}
              onOpenGeneralThread={handleOpenGeneralThread}
              onClose={handleClose}
            />
          )}
        </MessageTeacherDrawerProvider>
      </Drawer>
    </>
  );
}
